import { useState, useCallback } from 'react'
import _ from 'lodash'
import { trimData } from 'utils/commonFunctions'
import {
  CheckoutAdditionalField,
  CheckoutAdditionalFieldOption,
} from '../types'
import { MAX_FIELD_SELECT_OPTIONS_COUNT, INPUT_TYPE } from '../constants'

const getAdditionalOptions = (existingOptions: CheckoutAdditionalFieldOption[]): CheckoutAdditionalFieldOption[] => {
  const currentLength = existingOptions.length
  if (currentLength >= MAX_FIELD_SELECT_OPTIONS_COUNT) return []

  return Array.from(
    { length: MAX_FIELD_SELECT_OPTIONS_COUNT - currentLength },
    () => ({
      optionName: '',
      displayFlag: false,
    })
  )
}

type UseCheckoutAdditionalFieldEditorParams = {
  selectedField: CheckoutAdditionalField
  onSave: (field: CheckoutAdditionalField) => void
  onClose: () => void
  openError: (message: string, useNetworkError?: boolean) => void
}

export const useCheckoutAdditionalFieldEditor = ({
  selectedField,
  onSave,
  onClose,
  openError,
}: UseCheckoutAdditionalFieldEditorParams) => {
  const [defaultData] = useState<CheckoutAdditionalField>(() => ({ ...selectedField }))

  const [formData, setFormData] = useState<CheckoutAdditionalField>(() => {
    if (selectedField.inputType !== INPUT_TYPE.CHOICE) {
      return { ...selectedField }
    }

    const existingOptions = selectedField.options || []
    const additionalOptions = getAdditionalOptions(existingOptions)
    return {
      ...selectedField,
      options: [...existingOptions, ...additionalOptions]
    }
  })

  const getActualData = (field: CheckoutAdditionalField) => {
    if (field.inputType !== INPUT_TYPE.CHOICE) return { ...field, options: [] }
    return {
      ...field,
      options: field.options?.filter(opt => opt.optionName || opt.displayFlag) || []
    }
  }

  const isDirty = !_.isEqual(getActualData(defaultData), getActualData(formData))


  /**
   * 項目名
   */
  const handleChangeFieldName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFormData((prev) => ({ ...prev, displayName: value }))
  }, [])

  /**
   * 入力形式
   */
  const handleChangeInputType = useCallback(({ value }: { value: CheckoutAdditionalField["inputType"] }) => {
    setFormData((prev) => {
      const options = prev.options || []
      const newOptions = value === INPUT_TYPE.CHOICE ? [...options, ...getAdditionalOptions(options)] : options

      return {
        ...prev,
        inputType: value,
        options: newOptions
      }
    })
  }, [])

  /**
   * 選択式の選択肢名称
   */
  const handleChangeOptionName = useCallback((value: string, shouldDisplay: boolean, optionIndex: number) => {
    setFormData((prev) => {
      const newOptions = [...(prev.options || [])]
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        optionName: value,
        displayFlag: shouldDisplay,
      }
      return { ...prev, options: newOptions }
    })
  }, [])

  /**
   * 選択式の選択肢名称を空にする
   */
  const handleEmptyOption = useCallback((optionIndex: number) => {
    setFormData((prev) => {
      const newOptions = [...(prev.options || [])]
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        optionName: '',
        displayFlag: false,
      }
      return { ...prev, options: newOptions }
    })
  }, [])

  /**
   * 選択式の選択肢表示/非表示切替
   */
  const handleChangeVisibility = useCallback((optionIndex: number) => {
    setFormData((prev) => {
      const newOptions = [...(prev.options || [])]
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        displayFlag: !newOptions[optionIndex].displayFlag,
      }
      return { ...prev, options: newOptions }
    })
  }, [])

  /**
   * 登録
   * ・バリデーションチェック
   * ・選択式以外の場合 options を空配列
   * ・保存＆閉じる
   */
  const handleRegister = () => {
    const fieldTrim = trimData(formData)
    if (!fieldTrim.displayName) return openError('項目名は空にできません', false)

    if (formData.inputType === INPUT_TYPE.CHOICE) {
      const visibleOptions = (formData.options || []).filter((opt) => opt.displayFlag)

      if (visibleOptions.length === 0) return openError('選択肢は1つ以上必要です', false)
      if (visibleOptions.some((opt) => !opt.optionName)) return openError('選択肢の名称を未入力で表示にはできません', false)
    }

    const fieldToSave = formData.inputType === INPUT_TYPE.CHOICE
      ? formData
      : { ...formData, options: [] }

    onSave(fieldToSave)
    onClose()
  }

  return {
    formData,
    isDirty,
    handleChangeVisibility,
    handleEmptyOption,
    handleChangeFieldName,
    handleChangeInputType,
    handleChangeOptionName,
    handleRegister,
  }
}
